import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import "./MultiValueField.css";

function MultiValueField({
  values,
  onChange,
  inputValue,
  onInputChange,
  placeholder,
  suggestions = [],
  showSuggestions = false,
  onShowSuggestionsChange,
}) {
  const inputRowRef = useRef(null);
  const inputRef = useRef(null);
  const [overlayStyle, setOverlayStyle] = useState(null);
  const isOverlayOpen = showSuggestions && suggestions.length > 0;

  const addValue = (rawValue) => {
    const nextValue = String(rawValue || "").trim();
    if (!nextValue) {
      return;
    }

    const exists = values.some((value) => value.toLowerCase() === nextValue.toLowerCase());
    if (!exists) {
      onChange([...values, nextValue]);
    }

    onInputChange("");
    onShowSuggestionsChange?.(false);
    window.requestAnimationFrame(() => {
      inputRef.current?.focus();
    });
  };

  const removeValue = (valueToRemove) => {
    onChange(values.filter((value) => value !== valueToRemove));
  };

  const handleKeyDown = (event) => {
    const shouldAddValue = ["Enter", ",", "Tab"].includes(event.key);
    if (shouldAddValue && inputValue.trim()) {
      event.preventDefault();
      addValue(inputValue);
      return;
    }

    if (event.key === "Backspace" && !inputValue && values.length > 0) {
      onChange(values.slice(0, -1));
    }
  };

  const updateOverlayPosition = () => {
    if (!inputRowRef.current) {
      return;
    }

    const rect = inputRowRef.current.getBoundingClientRect();
    setOverlayStyle({
      top: rect.bottom + 8,
      left: rect.left,
      width: rect.width,
    });
  };

  useLayoutEffect(() => {
    if (!isOverlayOpen) {
      return;
    }

    updateOverlayPosition();
  }, [isOverlayOpen, suggestions.length, values.length, inputValue]);

  useEffect(() => {
    if (!isOverlayOpen) {
      return undefined;
    }

    const handleViewportChange = () => updateOverlayPosition();
    window.addEventListener("resize", handleViewportChange);
    window.addEventListener("scroll", handleViewportChange, true);

    return () => {
      window.removeEventListener("resize", handleViewportChange);
      window.removeEventListener("scroll", handleViewportChange, true);
    };
  }, [isOverlayOpen]);

  return (
    <div className={`multi-value-field${isOverlayOpen ? " multi-value-field-open" : ""}`}>
      <div
        className="multi-value-shell"
      >
        <div
          className="multi-value-input-row"
          ref={inputRowRef}
          onClick={() => inputRef.current?.focus()}
        >
          <input
            ref={inputRef}
            type="text"
            value={inputValue}
            onChange={(event) => {
              onInputChange(event.target.value);
              onShowSuggestionsChange?.(true);
            }}
            onFocus={() => onShowSuggestionsChange?.(true)}
            onBlur={() => window.setTimeout(() => onShowSuggestionsChange?.(false), 180)}
            onKeyDown={handleKeyDown}
            className="multi-value-input"
            placeholder={placeholder}
          />
        </div>

        <div
          className={`multi-value-results${values.length ? " multi-value-results-filled" : ""}`}
          onClick={() => inputRef.current?.focus()}
        >
          {values.length === 0 ? (
            <div className="multi-value-empty">Los valores agregados apareceran aqui.</div>
          ) : (
            <div className="multi-value-track">
              {values.map((value) => (
                <span key={value} className="multi-value-chip" title={value}>
                  <span className="multi-value-chip-text">{value}</span>
                  <button
                    type="button"
                    className="multi-value-chip-remove"
                    onClick={(event) => {
                      event.stopPropagation();
                      removeValue(value);
                    }}
                    aria-label={`Eliminar ${value}`}
                  >
                    ×
                  </button>
                </span>
              ))}
            </div>
          )}
        </div>
      </div>

      {isOverlayOpen && overlayStyle && createPortal(
        <ul
          className="autocomplete-suggestions multi-value-suggestions-portal"
          style={{
            top: `${overlayStyle.top}px`,
            left: `${overlayStyle.left}px`,
            width: `${overlayStyle.width}px`,
          }}
        >
          {suggestions.map((suggestion) => (
            <li
              key={suggestion}
              onMouseDown={(event) => {
                event.preventDefault();
                addValue(suggestion);
              }}
            >
              {suggestion}
            </li>
          ))}
        </ul>,
        document.body
      )}
    </div>
  );
}

export default MultiValueField;
